import { getAccessToken } from "../auth";

// Story jve-4-4 — the SPA half of frontend problem-tracking. Posts browser
// problem reports + web-vitals to the backend client-log sink (Story jve-4-3),
// which is authenticated-operator-only, rate/body-capped, and field-allow-listed.
// This module mirrors that allow-list on the client so we never send anything the
// server would reject, and is fire-and-forget so telemetry can never surface as
// an app error itself.

export type ClientLogType = "error" | "unhandledrejection" | "web-vital";

export interface ClientLogReport {
  type: ClientLogType;
  name?: string;
  message?: string;
  stack?: string;
  url?: string;
  value?: number;
}

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const SINK_PATH = "/client-logs";
const RELEASE_ID = import.meta.env.VITE_RELEASE_ID as string | undefined;

// Mirror the server-side @Size caps so a long stack/message is trimmed before it
// even leaves the browser (the sink would otherwise 413 a too-large body).
const NAME_CAP = 200;
const MESSAGE_CAP = 1000;
const STACK_CAP = 4000;

/** Strip the query string (+ fragment) — the URL path is all the sink keeps. */
export function stripQuery(url: string): string {
  return url.split("#")[0].split("?")[0];
}

/** A per-report cross-stack correlation id (the SPA half of jve-4-2). */
function correlationId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID — still allow-list-safe
  // ([A-Za-z0-9_-]{8,64}); the value is opaque, not security-sensitive.
  return `r-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/**
 * Send one problem/vital report. Builds only the allow-listed fields, caps their
 * length, strips the url query string, tags a correlation id, and swallows any
 * transport error — reporting must never throw into the app or loop on itself.
 */
export function reportProblem(report: ClientLogReport): void {
  const body = {
    type: report.type,
    name: report.name?.slice(0, NAME_CAP),
    message: report.message?.slice(0, MESSAGE_CAP),
    stack: report.stack?.slice(0, STACK_CAP),
    url: stripQuery(report.url ?? location.pathname),
    value: report.value,
    releaseId: RELEASE_ID,
  };

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Correlation-ID": correlationId(),
  };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    // keepalive so a report fired during page unload still flushes.
    void fetch(`${API_URL}${SINK_PATH}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      /* swallow — telemetry failures must stay invisible to the operator */
    });
  } catch {
    /* fetch unavailable / threw synchronously — never propagate */
  }
}
